/**
 * A yacht charter: a few hours out on the water off the city, a captain and a steward. You pay up front (the price
 * on the card before you board, localised like everything else), and what you do aboard feeds the life: drinks,
 * lying in the sun, the view from the bow, a swim off the back at anchor. It's an outing: fun and social go up,
 * your followers notice, and it's in your story.
 */
import { advance, spend, type LifeEvent, type LifeState } from './life.ts';
import { localPrice } from './character.ts';

/** Where the boats go out from, by city (made-up marina names over real waters). */
const MARINAS: Record<string, string> = {
  lagos: 'Five Cowries Marina, Lagos Lagoon', village: 'Port Vieux Charters, Marseille', paris: 'Seine Yacht Club, Pont de Grenelle',
  nyc: 'Hudson Point Marina, Manhattan', sf: 'Bayline Charters, San Francisco Bay', chicago: 'Lakefront Charters, Lake Michigan',
  shanghai: 'Huangpu Riverside Marina, Shanghai', beijing: 'Huangpu Riverside Marina, Shanghai', greatwall: 'Huangpu Riverside Marina, Shanghai',
  tokyo: 'Odaiba Bay Marina, Tokyo', kyoto: 'Odaiba Bay Marina, Tokyo', fuji: 'Odaiba Bay Marina, Tokyo',
  florence: 'Marina di Pisa Charters, Tuscany', rome: 'Porto Turistico di Roma, Ostia', tuscany: 'Marina di Pisa Charters, Tuscany',
  dubai: 'Dubai Harbour Charters, Dubai Marina', marina: 'Dubai Harbour Charters, Dubai Marina', deira: 'Dubai Harbour Charters, Dubai Marina',
};
export function marinaFor(region: string, hood: string): string {
  return MARINAS[region === 'lagos' ? 'lagos' : hood] ?? MARINAS[{ france: 'paris', us: 'nyc', china: 'shanghai', japan: 'tokyo' }[region] ?? 'lagos'];
}

/** Three hours, the boat and the crew (Lagos price; localPrice elsewhere). */
export const charterPrice = (s: LifeState) => localPrice(s, 1_200_000, 5_000);
export const CHARTER_HOURS = 3;

export interface Charter { since: number; drinks: number; swam: boolean; lounged: boolean; bow: boolean }
export const charterOf = (s: LifeState): Charter | null => s.charter ?? null;

/** Pay and board. */
export function charterYacht(s: LifeState): { ok: boolean; line: string } {
  const price = charterPrice(s);
  if (!spend(s, price, 'Yacht charter')) return { ok: false, line: `"It's ${price.toLocaleString()} for three hours, boss." You don't have it.` };
  s.charter = { since: s.minutes, drinks: 0, swam: false, lounged: false, bow: false };
  return { ok: true, line: 'The captain takes your hand at the stern and you step aboard. The engines rumble; the marina slides away.' };
}

/** Something aboard: a drink at the bar (on the house: it's in the charter), the cushions, the bow, a swim. */
export function aboard(s: LifeState, what: 'drink' | 'lounge' | 'bow' | 'swim'): { line: string; events: LifeEvent[] } {
  const c = s.charter;
  if (!c) return { line: '', events: [] };
  const need = (k: 'fun' | 'energy' | 'social' | 'hygiene', v: number) => { s.needs[k] = Math.max(0, Math.min(100, s.needs[k] + v)); };
  let line = '', mins = 10;
  if (what === 'drink') {
    c.drinks++;
    need('fun', c.drinks > 3 ? 2 : 6);
    line = c.drinks === 1 ? 'The steward pours you something cold with a slice of lime. "Enjoy the ride."' : c.drinks > 3 ? '"Maybe some water now?" the steward says, kindly.' : 'Another one, with the wind in your face.';
  } else if (what === 'lounge') {
    c.lounged = true; mins = 40;
    need('energy', 12); need('fun', 6);
    line = 'You lie back on the cushions. Sun, the engine\'s hum, the slap of water on the hull. You might have dozed off.';
  } else if (what === 'bow') {
    c.bow = true;
    need('fun', 8);
    line = 'At the bow the boat lifts and drops under you, spray on your arms, the whole city laid out along the shore.';
  } else {
    c.swam = true; mins = 30;
    need('fun', 12); need('energy', -6); need('hygiene', -5);
    s.skills.fitness = Math.min(100, s.skills.fitness + 0.6);
    line = 'Cold, then perfect. You float on your back and look up at the yacht, white against the sky.';
  }
  return { line, events: advance(s, mins, { busy: true }) };
}

/** Back at the marina: the rest of the three hours, and what the day was. */
export function endCharter(s: LifeState): { lines: string[]; events: LifeEvent[] } {
  const c = s.charter;
  if (!c) return { lines: [], events: [] };
  const left = Math.max(0, c.since + CHARTER_HOURS * 60 - s.minutes);
  const events = advance(s, left, { busy: true });
  s.needs.fun = Math.min(100, s.needs.fun + 15);
  s.needs.social = Math.min(100, s.needs.social + 8);
  s.stats.yachtDays = (s.stats.yachtDays ?? 0) + 1;
  const first = (s.stats.yachtDays ?? 0) === 1;
  s.history.push({ t: s.minutes, text: first ? 'Chartered a yacht for the first time.' : 'Another day out on a yacht.' });
  const lines = [first ? 'Your first time on a yacht. You will be talking about this for weeks.' : 'Another afternoon on the water.'];
  if (c.swam) lines.push('A swim off the back, in open water.');
  if (c.bow) lines.push('The view from the bow: worth a photo.');
  s.charter = undefined;
  return { lines, events };
}
